import {
  MonitorSmartphone,
  Shield,
  Server,
  Code2,
  Palette,
  Users,
  DollarSign,
  Scale,
  type LucideIcon,
} from 'lucide-react';
import { heatOf, isOpen, type Ticket } from './mock';

export type TeamType = 'it' | 'soc' | 'dev' | 'design' | 'generic';

export type Team = {
  id: string;
  slug: string;
  name: string;
  type: TeamType;
  icon: LucideIcon;
  hue: number; // identity token
  match: string[]; // ticket.team values that belong to this team
  members: string[];
  oncall: string | null;
  seedNum: number;
  seed: {
    open: number;
    breaching: number;
    unassigned: number;
    capacity: number; // 0..1 load
    avgResH: number;
  };
};

export const TEAMS: Team[] = [
  {
    id: '11111111-1111-1111-1111-111111111111',
    slug: 'it-service-desk',
    name: 'IT / Service Desk',
    type: 'it',
    hue: 215,
    icon: MonitorSmartphone,
    match: ['IT', 'Service Desk', 'Helpdesk'],
    members: [],
    oncall: null,
    seedNum: 3,
    seed: { open: 18, breaching: 2, unassigned: 4, capacity: 0.62, avgResH: 6.4 },
  },
  {
    id: '22222222-2222-2222-2222-222222222222',
    slug: 'security-soc',
    name: 'Security / SOC',
    type: 'soc',
    icon: Shield,
    hue: 268,
    match: [],
    members: [],
    oncall: null,
    seedNum: 7,
    seed: { open: 9, breaching: 3, unassigned: 1, capacity: 0.81, avgResH: 2.1 },
  },
  {
    id: '33333333-3333-3333-3333-333333333333',
    slug: 'platform-sre',
    name: 'Platform / SRE',
    type: 'dev',
    icon: Server,
    hue: 190,
    match: ['Platform SRE'],
    members: [],
    oncall: null,
    seedNum: 11,
    seed: { open: 12, breaching: 1, unassigned: 2, capacity: 0.74, avgResH: 4.8 },
  },
  {
    id: '44444444-4444-4444-4444-444444444444',
    slug: 'development',
    name: 'Development',
    type: 'dev',
    icon: Code2,
    hue: 150,
    match: [],
    members: [],
    oncall: null,
    seedNum: 5,
    seed: { open: 23, breaching: 0, unassigned: 6, capacity: 0.55, avgResH: 14.2 },
  },
  {
    id: '77777777-7777-7777-7777-777777777777',
    slug: 'design',
    name: 'Design',
    type: 'design',
    icon: Palette,
    hue: 322,
    match: [],
    members: [],
    oncall: null,
    seedNum: 9,
    seed: { open: 11, breaching: 0, unassigned: 3, capacity: 0.41, avgResH: 22.0 },
  },
  {
    id: '55555555-5555-5555-5555-555555555555',
    slug: 'hr-people-ops',
    name: 'HR / People Ops',
    type: 'generic',
    icon: Users,
    hue: 35,
    match: [],
    members: [],
    oncall: null,
    seedNum: 2,
    seed: { open: 7, breaching: 1, unassigned: 2, capacity: 0.48, avgResH: 30.0 },
  },
  {
    id: '66666666-6666-6666-6666-666666666666',
    slug: 'facilities',
    name: 'Facilities',
    type: 'generic',
    icon: DollarSign,
    hue: 95,
    match: [],
    members: [],
    oncall: null,
    seedNum: 13,
    seed: { open: 5, breaching: 0, unassigned: 1, capacity: 0.36, avgResH: 26.0 },
  },
  {
    id: '88888888-8888-8888-8888-888888888888',
    slug: 'legal',
    name: 'Legal',
    type: 'generic',
    icon: Scale,
    hue: 230,
    match: [],
    members: [],
    oncall: null,
    seedNum: 17,
    seed: { open: 3, breaching: 0, unassigned: 0, capacity: 0.28, avgResH: 40.0 },
  },
];

export type TeamStatus = 'healthy' | 'warning' | 'critical';

export type Vitals = {
  total: number;
  open: number;
  assigned: number;
  unassigned: number;
  in_progress: number;
  pending: number;
  hold: number;
  resolved: number;
  closed: number;
  cancelled: number;
  breaching: number;
  capacity: number;
  avgResH: number;
  heat: number;
  status: TeamStatus;
  spark: number[];
};

/** 24-point sparkline derived from real tickets — shaped by creation hour + heat. */
function sparkFromTickets(ts: Ticket[], now: number): number[] {
  const buckets = new Array(24).fill(0);
  for (const t of ts) {
    const hr = new Date(t.createdAt).getHours();
    buckets[hr] += 1 + heatOf(t, now);
  }
  const max = Math.max(...buckets, 1);
  return buckets.map((b) => 0.08 + (b / max) * 0.92);
}

export function teamTickets(team: Team, tickets: Ticket[]): Ticket[] {
  return tickets.filter((t) => {
    // If ticket has no team, route it to IT / Service Desk by default to prevent dropping
    if (!t.teamId && (!t.team || t.team === '—')) {
      return team.id === '11111111-1111-1111-1111-111111111111';
    }
    return t.teamId === team.id || t.team === team.name || team.match.includes(t.team);
  });
}

export function vitalsOf(team: Team, tickets: Ticket[], now = Date.now()): Vitals {
  const mine = teamTickets(team, tickets);
  const openT = mine.filter(isOpen);
  const total = mine.length;
  const open = openT.length;
  const assigned = openT.filter((t) => !!t.assignee).length;
  const unassigned = openT.length - assigned;
  const in_progress = mine.filter((t) => t.state === 'In Progress').length;
  const pending = mine.filter((t) => t.state === 'Pending').length;
  const hold = mine.filter((t) => t.state === 'On Hold').length;
  const resolved = mine.filter((t) => t.state === 'Resolved').length;
  const closed = mine.filter((t) => t.state === 'Closed').length;
  const cancelled = mine.filter((t) => t.state === 'Cancelled').length;
  
  const breaching = openT.filter((t) => heatOf(t, now) >= 0.65).length;
  const capacity = Math.min(1, open / Math.max(1, team.members.length * 3));
  const ages = openT.map((t) => (now - t.createdAt) / 3_600_000);
  const avgResH = ages.length ? Math.round((ages.reduce((a, b) => a + b, 0) / ages.length) * 10) / 10 : 0;
  const breachPressure = open > 0 ? breaching / open : 0;
  const heat = Math.max(0, Math.min(1, breachPressure * 0.7 + capacity * 0.35));
  const status: TeamStatus = heat >= 0.7 ? 'critical' : heat >= 0.42 ? 'warning' : 'healthy';
  
  return {
    total,
    open,
    assigned,
    unassigned,
    in_progress,
    pending,
    hold,
    resolved,
    closed,
    cancelled,
    breaching,
    capacity,
    avgResH,
    heat,
    status,
    spark: sparkFromTickets(mine, now),
  };
}

export function getTeam(id: string): Team | undefined {
  return TEAMS.find((t) => t.id === id);
}
